import './QueryPills.css'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import Button from '../Button/Button'
import Icon from '../Icon2'
import Pills from '../Pills/Pill'
import QueryPillsV2 from './QueryPillsV2'
import { TruncateProps } from 'components/Truncate/Truncate'
import Line from '../Line/Line'
import Dropdown from '../DropDown/Dropdown'

interface MoreFilterActionItem {
	label: string | ReactNode
	value: string
	id: string
	action: (e: React.MouseEvent<HTMLButtonElement>) => void
}

interface MoreFilterAction {
	actionList: MoreFilterActionItem[]
	/**
	 * Width of the pillsWrapper container to render(default is 0.5 for 50%)
	 */
	pillsRenderContainerWidth?: number
	viewAllFilterState?: {
		showViewAllFilterDropdown?: boolean
		openOrCloseViewAllDropdown?: (show: boolean) => void
		viewAllFilters: (close: () => void) => JSX.Element
	}
	defaultSelected?: { id: string; label: string }
}

export interface IQueryPillsComponentResponsiveness {
	isResponsive?: boolean
	screen?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
	moreFilterAction?: MoreFilterAction
}

export type QueryPillsProps = {
    testId?: String;
    onClearAllFilters?: Function;
    filterPillsArr?: any;
    onPillsCancelClick?: Function;
    onHideFilterClick?: Function;
    version?: 'v1' | 'v2';
    dropDownContent?: (closeDropdown: () => void) => ReactNode;
    hidePillsTrailingIcon?: boolean;
    leadingIcon?: string;
    onPillsClick?: Function;
	highlightText?: string
	filterCount?: number
	truncateOptions?: TruncateProps
	componentResponsiveness?: IQueryPillsComponentResponsiveness
	isDisabled?: boolean
}

const QueryPills = (props: QueryPillsProps) => {
	const {
		testId = 'cs-query-pills',
		onClearAllFilters,
		filterPillsArr = [],
		onPillsCancelClick,
		onHideFilterClick,
		version,
		dropDownContent,
		hidePillsTrailingIcon = false,
		leadingIcon,
		onPillsClick,
		highlightText,
		filterCount,
		truncateOptions,
		componentResponsiveness = {},
		isDisabled
	} = props

	const { moreFilterAction } = componentResponsiveness
	const { pillsRenderContainerWidth = 0.5 } = moreFilterAction || {}

	const [hideFilters, setHideFilters] = useState(false)
	const isResponsive = componentResponsiveness.isResponsive && version !== 'v2'

	// Dropdown refs and state
	const viewAllFilterdropdownRef = useRef<HTMLDivElement>(null)
	const triggerViewAllFilterBtnRef = useRef<HTMLDivElement>(null)
	const triggerOverFlowFiltersRef = useRef<HTMLButtonElement>(null)
	const overFlowFiltersdropdownRef = useRef<HTMLDivElement>(null)
	const moreFilterActionsDropdownRef = useRef<HTMLDivElement>(null)

	const [viewAllFilterDropdownStyles, setViewAllFilterDropdownStyles] = useState<React.CSSProperties>({})
	const [overFlowFiltersDropdownStyles, setOverFlowFiltersDropdownStyles] = useState<React.CSSProperties>({})
	const [moreFilterActionsDropdownStyles, setMoreFilterActionsDropdownStyles] = useState<React.CSSProperties>({})

	// Pills state for responsiveness
	const [isShowMoreFilterButtonEnabled, setIsShowMoreFilterButtonEnabled] = useState(false)
	const [showOverFlowFilterPills, setShowOverFlowFilterPills] = useState(false)
	const [visibleFilterPills, setVisibleFilterPills] = useState<any[]>(filterPillsArr)
	const [overflowFilterPills, setOverflowFilterPills] = useState<any[]>([])
	const [overFlowCount, setOverFlowCount] = useState<number>(0)
	const [showMoreFilterOptionsDropdown, setShowMoreFilterOptionsDropdown] = useState(false)

	const pillsWrapperRef = useRef<HTMLDivElement | null>(null)
	const pillRefs = useRef<(HTMLDivElement | null)[]>([])

	// Hide filters handler
	const onHideFilters = () => {
		setHideFilters(!hideFilters)
		onHideFilterClick && onHideFilterClick(!hideFilters)
	}

	// Dropdown positioning
	const updateOverFlowDropdownPosition = () => {
		if (!triggerOverFlowFiltersRef.current || !overFlowFiltersdropdownRef.current) return
		const rect = triggerOverFlowFiltersRef.current.getBoundingClientRect()
		const dropdownWidth = overFlowFiltersdropdownRef.current.offsetWidth || 0

		setOverFlowFiltersDropdownStyles({
			top: rect.bottom + 2,
			left: rect.right - dropdownWidth
		})
	}

	const updateViewAllDropdownPosition = () => {
		if (!triggerViewAllFilterBtnRef.current || !viewAllFilterdropdownRef.current) return
		const rect = triggerViewAllFilterBtnRef.current.getBoundingClientRect()
		const dropdownWidth = viewAllFilterdropdownRef.current.offsetWidth || 0
		setViewAllFilterDropdownStyles({
			top: rect.bottom + 2,
			left: rect.left + triggerViewAllFilterBtnRef.current.offsetWidth - dropdownWidth
		})
	}

	const updateMoreFilterActionsDropdownPosition = () => {
		if (!triggerViewAllFilterBtnRef.current || !moreFilterActionsDropdownRef.current) return
		const rect = triggerViewAllFilterBtnRef.current.getBoundingClientRect()
		// const dropdownWidth = moreFilterActionsDropdownRef.current.offsetWidth || 0
		setMoreFilterActionsDropdownStyles({
			top: rect.top - 2,
			left: rect.right
		})
	}

	// Handle outside click for dropdowns
	const handleClickOutside = (event: MouseEvent) => {
		if (viewAllFilterdropdownRef.current && !viewAllFilterdropdownRef.current.contains(event.target as Node)) {
			moreFilterAction?.viewAllFilterState?.openOrCloseViewAllDropdown?.(false)
		}
		if (!moreFilterActionsDropdownRef.current?.contains(event.target as Node)) {
			setShowMoreFilterOptionsDropdown(false)
		}
	}

	// Effect for dropdown positioning and outside click handling
	useEffect(() => {
		const canViewAllDropdown =
			moreFilterAction?.viewAllFilterState?.showViewAllFilterDropdown && triggerViewAllFilterBtnRef.current
		const canOverflowDropdown =
			showOverFlowFilterPills && overflowFilterPills.length > 0 && triggerOverFlowFiltersRef.current
		const canMoreFilterActionsDropdown =
			showMoreFilterOptionsDropdown && moreFilterAction?.actionList?.length > 0 && triggerViewAllFilterBtnRef.current

		if (!canViewAllDropdown && !canOverflowDropdown && !canMoreFilterActionsDropdown) return

		canViewAllDropdown && updateViewAllDropdownPosition()
		canOverflowDropdown && updateOverFlowDropdownPosition()
		canMoreFilterActionsDropdown && updateMoreFilterActionsDropdownPosition()

		window.addEventListener('resize', updateViewAllDropdownPosition)
		document.addEventListener('scroll', updateViewAllDropdownPosition, true)
		document.addEventListener('mousedown', handleClickOutside)
		window.addEventListener('resize', updateOverFlowDropdownPosition)
		document.addEventListener('scroll', updateOverFlowDropdownPosition, true)
		window.addEventListener('resize', updateMoreFilterActionsDropdownPosition)
		document.addEventListener('scroll', updateMoreFilterActionsDropdownPosition, true)

		return () => {
			window.removeEventListener('resize', updateViewAllDropdownPosition)
			document.removeEventListener('scroll', updateViewAllDropdownPosition, true)
			document.removeEventListener('mousedown', handleClickOutside)
			window.removeEventListener('resize', updateOverFlowDropdownPosition)
			document.removeEventListener('scroll', updateOverFlowDropdownPosition, true)
			window.removeEventListener('resize', updateMoreFilterActionsDropdownPosition)
			document.removeEventListener('scroll', updateMoreFilterActionsDropdownPosition, true)
		}
	}, [
		moreFilterAction?.viewAllFilterState?.showViewAllFilterDropdown,
		showOverFlowFilterPills,
		overflowFilterPills.length,
		showMoreFilterOptionsDropdown
	])

	// Responsive pills calculation
	const [pillsWrapperWidth, setPillsWrapperWidth] = useState<number>(0)

	// Resize observer to track pills wrapper width
	useEffect(() => {
		if (!pillsWrapperRef.current) return
		const node = pillsWrapperRef.current

		const resizeObserver = new window.ResizeObserver((entries) => {
			for (let entry of entries) {
				if (entry.contentRect) setPillsWrapperWidth(entry.contentRect.width)
			}
		})
		resizeObserver.observe(node)
		setPillsWrapperWidth(node.offsetWidth)

		return () => resizeObserver.disconnect()
	}, [pillsWrapperRef])

	// Reset pills when isResponsive changes
	useEffect(() => {
		if (!isResponsive) {
			setOverflowFilterPills([])
			setVisibleFilterPills(filterPillsArr)
			setShowOverFlowFilterPills(false)
			setIsShowMoreFilterButtonEnabled(false)
			setOverFlowCount(0)
		}
	}, [filterPillsArr.length, isResponsive])

	// Calculate visible/overflow pills based on width and refs
	const calculateVisiblePills = () => {
		if (!isResponsive) return
		if (filterPillsArr.length === 0 || !pillsWrapperRef.current) return
		const containerWidth = pillsWrapperRef.current.parentElement?.offsetWidth || pillsWrapperRef.current.offsetWidth
		// const showMoreButtonWidth = 110 // Space for show more button

		let totalWidth = 0
		let visibleCount = 0
		const pillElements = pillsWrapperRef.current?.querySelectorAll('.Pill--single') || []
		for (let i = 0; i < pillElements.length; i++) {
			const pill = pillElements[i]
			const pillWidth = pill.offsetWidth + 12 // 12px for padding
			// Check if this pill would fit (reserve space for show more if there are remaining pills)
			const wouldOverflow = totalWidth + pillWidth > containerWidth * pillsRenderContainerWidth // Default 50% of container width for pills

			if (!wouldOverflow) {
				totalWidth += pillWidth
				visibleCount++
			} else {
				break
			}
		}

		const newVisiblePills = filterPillsArr.slice(0, visibleCount + 1)
		const newOverflowPills = filterPillsArr.slice(visibleCount + 1)
		setVisibleFilterPills(newVisiblePills)
		setOverflowFilterPills(newOverflowPills)
		setOverFlowCount(newOverflowPills.length || 0)
		setIsShowMoreFilterButtonEnabled(newOverflowPills.length > 0)
	}
	useEffect(() => {
		calculateVisiblePills()
	}, [pillsWrapperWidth, filterPillsArr, isResponsive, componentResponsiveness, truncateOptions])

	// Render nothing if no visible pills && no leading icon
	if (!filterPillsArr.length && !leadingIcon) return null
	return (
		<div data-test-id={testId} className={`QueryPills ${isResponsive ? 'QueryPills--resp_screen' : ''}`}>
			{!isResponsive ? (
				<>
					<div
						className={`QueryPills__filter-pills ${version} flex ${hideFilters ? 'QueryPills__filter-pills--hide' : 'QueryPills__filter-pills--show'
							}${isResponsive ? ' QueryPills__filter-pills--show--resp_screen' : ''}`}>
						{version === 'v2' ? (
							<QueryPillsV2 {...props} canTruncate={true} />
						) : (
							<>
								{/* used as a icon representation before the pills */}
								{leadingIcon && (
									<>
										<Pills
											className="QueryPills__leading-icon"
											status="default"
											isSingleton
											key={leadingIcon}
											variant="label"
											shouldHaveBorder={false}
											stroke=""
											canTruncate={true}
											testId={`${testId}-item-${leadingIcon}`}
											items={[
												{
													id: leadingIcon,
													text: '',
													trailingIcon: hidePillsTrailingIcon && (
														<Icon
															icon={leadingIcon}
															version="v2"
															size="extraSmall"
															onClick={() => { }}
															testId={`${testId}-item-${leadingIcon}`}
														/>
													),
													isPillDisabled: isDisabled,
												}
											]}
											dropDownContent={dropDownContent}
											onPillClickHandler={() => onPillsClick?.({ field: 'leadingIcon' })}
										/>
										<div className="QueryPills__leading-icon-separator" />
									</>
								)}
								{/* Pills renderering based on the input */}
								{filterPillsArr.map((pillsObj, i) => {
									return (
										<Pills
											status="default"
											isSingleton
											key={i}
											variant="label"
											shouldHaveBorder={false}
											stroke=""
											canTruncate={true}
											testId={`${testId}-item-${i}`}
											items={[
												{
													id: i,
													text: `${pillsObj.field} ${pillsObj.operator} ${pillsObj.value}`,
													trailingIcon: !hidePillsTrailingIcon && (
														<Icon
															icon="CloseNoborder"
															version="v2"
															size="extraSmall"
															onClick={(e) => onPillsCancelClick(e, pillsObj)}
															testId={`${testId}-item-${i}-cancel`}
														/>
													),
													leadingIcon: pillsObj?.leadingIcon,
													badgeText: pillsObj?.badgeText,
													isPillDisabled: pillsObj?.isPillDisabled || isDisabled,
													isSelected: pillsObj?.isSelected
												}
											]}
											onPillClickHandler={() => {
												onPillsClick?.(pillsObj)
											}}
											dropDownContent={dropDownContent}
											highlightText={highlightText}
											truncateOptions={truncateOptions}
										/>
									)
								})}
							</>
						)}
					</div>
				</>
			) : (
				<>
					<div
						className={`${isResponsive ? 'QueryPills__filter-pills--resp_screen flex-justify flex-v-center' : ''}`}
						{...(isResponsive ? { ref: pillsWrapperRef } : {})}>
						<div className={isResponsive ? 'flex' : ''}>
							<div
								className={`QueryPills__filter-pills ${version} flex ${hideFilters ? 'QueryPills__filter-pills--hide' : 'QueryPills__filter-pills--show'
									} ${isResponsive ? 'QueryPills__filter-pills--show--resp_screen' : ''}`}>
								<div className={`${isResponsive ? 'QueryPills__filter-pills--resp_screen flex-v-center' : ''}`}>
									{visibleFilterPills.map((pillsObj, i) => (
										<div key={i} {...(isResponsive ? { ref: (el) => (pillRefs.current[i] = el) } : {})}>
											<Pills
												status="default"
												isSingleton
												key={i}
												variant="label"
												shouldHaveBorder={false}
												stroke=""
												canTruncate={true}
												testId={`${testId}-item-${i}`}
												items={[
													{
														id: i,
														text: `${pillsObj.field} ${pillsObj.operator} ${pillsObj.value}`,
														trailingIcon: !hidePillsTrailingIcon && (
															<Icon
																icon="CloseNoborder"
																version="v2"
																size="extraSmall"
																onClick={(e) => onPillsCancelClick?.(e, pillsObj)}
																testId={`${testId}-item-${i}-cancel`}
															/>
														),
														leadingIcon: pillsObj?.leadingIcon,
														badgeText: pillsObj?.badgeText,
														isPillDisabled: pillsObj?.isPillDisabled || isDisabled,
														isSelected: pillsObj?.isSelected
													}
												]}
												onPillClickHandler={() => onPillsClick?.(pillsObj)}
												dropDownContent={dropDownContent}
												highlightText={highlightText}
												truncateOptions={truncateOptions}
												isResponsive={isResponsive}
											/>
										</div>
									))}
								</div>
							</div>
							{isResponsive && overFlowCount > 0 && isShowMoreFilterButtonEnabled && (
								<Button
									version="v2"
									buttonType="tertiary"
									onClick={() => setShowOverFlowFilterPills((prev) => !prev)}
									testId={`${testId}-show-more-btn`}
									size="small"
									buttonRef={triggerOverFlowFiltersRef}
									className={`QueryPills__overflow-show-more-btn--resp_screen`}
									disabled={isDisabled}
									>
									{`Show ${overFlowCount} More`}
								</Button>
							)}
							{overflowFilterPills.length > 0 &&
								showOverFlowFilterPills &&
								ReactDOM.createPortal(
									<div
										className="OverFlowFilterDropdown--resp_screen"
										style={overFlowFiltersDropdownStyles}
										data-test-id={`${testId}-overflow-filter-dropdown`}>
										<div
											className="OverFlowFilterDropdown--resp_screen__content"
											ref={overFlowFiltersdropdownRef}
											onClick={(e) => e.stopPropagation()}>
											<div
												className="OverFlowFilterDropdown--resp_screen__header"
												data-test-id={`${testId}-overflow-filter-dropdown-header`}>
												<div>
													<h3>
														Selected Filters{' '}
														<span className="OverFlowFilterDropdown--resp_screen__header__badge">
															{filterPillsArr.length}
														</span>
													</h3>
												</div>
												<Icon
													icon="CloseNoborder"
													version="v2"
													size="extraSmall"
													onClick={() => setShowOverFlowFilterPills(false)}
													testId={`${testId}-overflow-filter-dropdown-close-icon`}
												/>
											</div>
											<div className="QueryPills" data-test-id={`${testId}-overflow-filter-dropdown-pills-wrapper`}>
												<div className="QueryPills__filter-pills flex QueryPills__filter-pills--show">
													{overflowFilterPills.map((pillsObj, i) => (
														<div key={i}>
															<Pills
																status="default"
																isSingleton
																key={i}
																variant="label"
																shouldHaveBorder={false}
																stroke=""
																canTruncate={true}
																testId={`${testId}-overflow-item-${i}`}
																items={[
																	{
																		id: i,
																		text: `${pillsObj.field} ${pillsObj.operator} ${pillsObj.value}`,
																		trailingIcon: !hidePillsTrailingIcon && (
																			<Icon
																				icon="CloseNoborder"
																				version="v2"
																				size="extraSmall"
																				onClick={(e) => onPillsCancelClick?.(e, pillsObj)}
																				testId={`${testId}-item-${i}-cancel`}
																			/>
																		),
																		badgeText: pillsObj?.badgeText,
																		isPillDisabled: pillsObj?.isPillDisabled || isDisabled,
																		isSelected: pillsObj?.isSelected
																	}
																]}
																onPillClickHandler={() => onPillsClick?.(pillsObj)}
																dropDownContent={dropDownContent}
																highlightText={highlightText}
																isResponsive={isResponsive}
																truncateOptions={truncateOptions}
															/>
														</div>
													))}
												</div>
											</div>
											<Line type="solid" />
											<div
												className="OverFlowFilterDropdown--resp_screen__footer"
												data-test-id={`${testId}-overflow-filter-dropdown-footer`}>
												<Button
													testId={`${testId}-overflow-filter-dropdown-footer-goBack-btn`}
													buttonType="tertiary"
													version="v2"
													onClick={() => setShowOverFlowFilterPills(false)}>
													Cancel
												</Button>
											</div>
										</div>
									</div>,
									document.body
								)}
						</div>
						<div
							className={`QueryPills__actions flex-justify ${isResponsive ? 'QueryPills__actions--resp_screen' : ''
								}`}>
							{!isResponsive ? (
								<>
									<Button
										version="v2"
										buttonType="tertiary"
										iconAlignment="right"
										onClick={onClearAllFilters}
										testId={`${testId}-clear-all`}
										size="small">
										{`Clear All Filters (${filterCount !== undefined ? filterCount : filterPillsArr.length})`}
									</Button>
									<Button
										version="v2"
										buttonType="tertiary"
										iconAlignment="right"
										icon="v2-CaretCircleRight"
										className={` ${hideFilters ? 'QueryPills__actions--hide' : 'QueryPills__actions--visible'}`}
										onClick={onHideFilters}
										testId={`${testId}-${hideFilters ? 'show' : 'hide'}-filters`}
										size="small">
										{`${hideFilters ? 'Show' : 'Hide'} All Filters`}
									</Button>
								</>
							) : (
								<>
									<Button
										buttonRef={triggerViewAllFilterBtnRef}
										version="v2"
										buttonType="click"
										className={`QueryPills__action--resp_screen--more-filter-action-btn`}
										testId={`${testId}-more-filter-actions-btn`}
										size="small"
										onClick={() => setShowMoreFilterOptionsDropdown(true)}
										disabled={isDisabled}>
										More Filter Actions
									</Button>
									{showMoreFilterOptionsDropdown && moreFilterAction?.actionList?.length > 0 && isResponsive
										? ReactDOM.createPortal(
											<div
												style={moreFilterActionsDropdownStyles}
												ref={moreFilterActionsDropdownRef}
												className={`QueryPills__action--resp_screen--more-filter-action-dropdown-wrapper`}>
												<Dropdown
													isOpen={showMoreFilterOptionsDropdown}
													testId={`${testId}-more-filter-actions-dropdown`}
													className={`QueryPills__action--resp_screen--more-filter-action-dropdown`}
													version={'v2'}
													type="click"
													dropDownPosition="left"
													// portalZindex={1001}
													list={moreFilterAction?.actionList || []}
													closeAfterSelect={true}
													canCloseOnClickOutside={true}
													highlightActive={true}
													isDisabled={false}></Dropdown>
											</div>,
											document.body
										)
										: null}
									{moreFilterAction?.viewAllFilterState?.showViewAllFilterDropdown &&
										ReactDOM.createPortal(
											<div className="ViewAllFilterDropdown--resp_screen" style={viewAllFilterDropdownStyles}>
												<div
													className="ViewAllFilterDropdown--resp_screen__content"
													ref={viewAllFilterdropdownRef}
													onClick={(e) => e.stopPropagation()}>
													{moreFilterAction.viewAllFilterState.viewAllFilters(() =>
														moreFilterAction.viewAllFilterState.openOrCloseViewAllDropdown?.(false)
													)}
												</div>
											</div>,
											document.body
										)}
								</>
							)}
						</div>
					</div>
				</>
			)}
			{/* Clear all filters and hide filters bar */}
			{!isResponsive ? (
				<div className="QueryPills__actions flex-justify">
					<Button
						version="v2"
						buttonType="tertiary"
						iconAlignment="right"
						onClick={onClearAllFilters}
						testId={`${testId}-clear-all`}
						size="small"
						disabled={isDisabled}>
						{`Clear All Filters (${filterCount !== undefined ? filterCount : filterPillsArr.length})`}
					</Button>
					<Button
						version="v2"
						buttonType="tertiary"
						iconAlignment="right"
						icon="v2-CaretCircleRight"
						className={` ${hideFilters ? 'QueryPills__actions--hide' : 'QueryPills__actions--visible'}`}
						onClick={onHideFilters}
						testId={`${testId}-${hideFilters ? 'show' : 'hide'}-filters`}
						size="small">
						{`${hideFilters ? 'Show' : 'Hide'} All Filters`}
					</Button>
				</div>
			) : null}
		</div>
	)
}

export default QueryPills
